import { magicLoginConsume } from "@/services/auth";
import {
  accessTokenAtom,
  dataContextAtom,
  isAnonymousUserAtom,
  setAuthAtom,
  showsSessionExpiredModalAtom,
  store,
} from "@/utils/atoms";
import { getAuthApiErrorText } from "@/utils/authApiError";
import { fetchPortalAccessToken } from "@/utils/fetchPortalAccessToken";
import type { MagicLinkConsumeError } from "@/utils/magicLinkErrors";

const classifyConsumeError = (error: unknown): MagicLinkConsumeError => {
  const message = getAuthApiErrorText(error);

  if (message.includes("link already used")) {
    return "used_link";
  }

  if (message.includes("link expired")) {
    return "expired_link";
  }

  if (message.includes("invalid hash")) {
    return "invalid_link";
  }

  return "generic";
};

let inFlightConsume: Promise<{
  success: boolean;
  error?: MagicLinkConsumeError;
}> | null = null;
let inFlightHash: string | null = null;

const runConsume = async (
  hash: string,
): Promise<{ success: boolean; error?: MagicLinkConsumeError }> => {
  try {
    const portalToken = await fetchPortalAccessToken();
    const tokenData = await magicLoginConsume(
      { hash },
      { headers: { Authorization: `Bearer ${portalToken}` } },
    );

    if (!tokenData.access_token || !tokenData.refresh_token) {
      return { success: false, error: "generic" };
    }

    localStorage.clear();

    store.set(dataContextAtom, (prev) => ({
      ...prev,
      isEmailVerified: true,
    }));

    store.set(setAuthAtom, {
      accessToken: tokenData.access_token,
      refreshToken: tokenData.refresh_token,
      isAnonymous: false,
    });

    store.set(showsSessionExpiredModalAtom, undefined);

    return { success: true };
  } catch (error) {
    const consumeError = classifyConsumeError(error);

    // First consume succeeded; a duplicate call only sees "already used".
    if (
      consumeError === "used_link" &&
      store.get(accessTokenAtom) &&
      !store.get(isAnonymousUserAtom)
    ) {
      return { success: true };
    }

    return { success: false, error: consumeError };
  }
};

/**
 * Single-flight magic-link consume. Prevents duplicate API calls when React re-runs effects.
 */
export const consumeMagicLinkSession = (hash: string) => {
  if (inFlightConsume && inFlightHash === hash) {
    return inFlightConsume;
  }

  inFlightHash = hash;
  inFlightConsume = runConsume(hash).finally(() => {
    inFlightConsume = null;
    inFlightHash = null;
  });

  return inFlightConsume;
};
